import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listRegistryLaborCos, updateLaborCo, addLaborCoContact, updateLaborCoContact, deleteLaborCoContact,
  reissueLaborPasscode, phoneHealth, getMe, type RegistryLaborCo, type LaborCoContact, type ApiError, type MeResponse,
} from '../lib/api';
import { Skeleton } from '../components/ui/Skeleton';
import { toast } from '../components/ui/Toast';

// Fallbacks so the page never blanks before /me lands (or if an old server omits the lists).
const FALLBACK_SUB_TYPES = ['装修', '拆除', '空调', '消防', '防水', '木作'];
const FALLBACK_CITIES = ['北京', '上海', '武汉', '成都', '西安', '杭州'];
const NO_CITY = '__none';          // 城市 filter pill for suppliers with no 城市 on file
const NO_TYPE = '__none';          // 类型 filter pill for suppliers with no 类型 on file
const NEW_CITY = '__new';          // the ＋新城市 option in CityCell's <select>
type SortKey = 'code' | 'type' | 'city' | 'age' | 'engagements';
/** localeCompare in 中文 order, with blanks always at the bottom of the ascending list. */
function blankLast(a: string, b: string): number {
  if (!a || !b) return a ? -1 : b ? 1 : 0;
  return a.localeCompare(b, 'zh');
}
const PILL_BTN: React.CSSProperties = { border: 'none', cursor: 'pointer', fontFamily: 'inherit' };
/** An empty cell that INVITES the click (Eric fb_edd6df83: a muted 「—」 read as text, not a control). */
const DASH_CHIP: React.CSSProperties = {
  border: '1px dashed var(--rule)', borderRadius: 999, padding: '1px 9px',
  background: 'none', cursor: 'pointer', fontFamily: 'inherit',
};
/** Text that is really a button (the set 城市 cell) — no chrome until you click it. */
const TEXT_BTN: React.CSSProperties = {
  border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
};
// The taxonomy fields are new; a stale bundle, a cached payload or a not-yet-deployed server can
// still hand us rows without them. There is no ErrorBoundary above this page, so every read of
// sub_types/history goes through a guard — missing data degrades to 无类型 / 新供应商, never a blank page.
const NO_HISTORY = { past_engagements: 0, awarded: 0 };
const historyOf = (c: RegistryLaborCo) => c.history ?? NO_HISTORY;
const subTypesOf = (c: RegistryLaborCo) => c.sub_types ?? [];

/**
 * 供应商名录 / Suppliers — the login roster. Each supplier can have up to 5 named 登录联系人 (spec
 * 2026-07-07); every contact signs into the portal with their own SMS-verified phone, and portal
 * actions show WHO acted (公司名 · 人名). This page flags who can't log in yet (no contacts + a
 * missing/invalid/duplicate legacy number) and lets staff add/fix contacts inline.
 */
export default function Suppliers() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<RegistryLaborCo[]>({ queryKey: ['registry-labor-cos'], queryFn: listRegistryLaborCos });
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const [q, setQ] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [typeSel, setTypeSel] = useState<Set<string>>(new Set());
  const [citySel, setCitySel] = useState<Set<string>>(new Set());
  const [ageSel, setAgeSel] = useState<'all' | 'new' | 'old'>('all');
  const [sort, setSort] = useState<SortKey>('code');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const refresh = () => qc.invalidateQueries({ queryKey: ['registry-labor-cos'] });

  // The pickable 类型/城市 lists are org settings (GM edits them in 管理), and ride along on /me.
  const subTypeOptions = me?.settings?.registry?.supplier_types?.sub_types ?? FALLBACK_SUB_TYPES;
  const cityOptions = me?.settings?.registry?.cities ?? FALLBACK_CITIES;
  // Eric 9/18 (reissue passcode): 重发口令 is gm + 预算 server-side (requireRole('gm','budget') — 改改 is who
  // suppliers phone) — mirror that here so 采购 etc. never see a button that can only 403. Same /me read
  // the pickable lists ride on. The prop keeps its name; it gates the reissue button, nothing else.
  const isGm = me?.account?.role === 'gm' || me?.account?.role === 'budget';

  // duplicate detection only matters for CONTACT-LESS rows (legacy fallback numbers) — contacts are
  // server-deduped by a unique index, so having ≥1 contact means the supplier can log in.
  const dupIds = useMemo(() => {
    const by = new Map<string, string[]>();
    for (const c of data ?? []) {
      if ((c.contacts?.length ?? 0) > 0 || phoneHealth(c.phone) !== 'ok') continue;
      const p = (c.phone ?? '').replace(/\D/g, '').replace(/^86(?=\d{11}$)/, '');
      (by.get(p) ?? by.set(p, []).get(p)!).push(c.id);
    }
    const dup = new Set<string>();
    for (const ids of by.values()) if (ids.length > 1) ids.forEach((i) => dup.add(i));
    return dup;
  }, [data]);

  const rowState = (c: RegistryLaborCo): 'ok' | 'missing' | 'invalid' | 'duplicate' => {
    if ((c.contacts?.length ?? 0) > 0) return 'ok';
    return dupIds.has(c.id) ? 'duplicate' : phoneHealth(c.phone);
  };

  const filtered = (data ?? []).filter((c) => {
    const hay = `${c.name}${c.internal_code ?? ''}${c.primary_contact ?? ''}${c.phone ?? ''}${(c.contacts ?? []).map((x) => `${x.name}${x.phone}`).join('')}`;
    if (q && !hay.toLowerCase().includes(q.toLowerCase())) return false;
    if (onlyProblems && rowState(c) === 'ok') return false;
    // 多选 = match any; the 未填 pill matches the rows that carry no 类型 at all
    const ts = subTypesOf(c);
    if (typeSel.size && !(ts.some((t) => typeSel.has(t)) || (ts.length === 0 && typeSel.has(NO_TYPE)))) return false;
    if (citySel.size && !citySel.has(c.city ?? NO_CITY)) return false;
    if (ageSel !== 'all' && (historyOf(c).past_engagements === 0) !== (ageSel === 'new')) return false;
    return true;
  });
  // 编号 order is the server's (active first, then internal_code) — ▼ just reverses it.
  const rows = sort === 'code'
    ? (dir === 'asc' ? filtered : [...filtered].reverse())
    : [...filtered].sort((a, b) => {
      const [ha, hb] = [historyOf(a), historyOf(b)];
      const n = sort === 'type' ? blankLast(subTypesOf(a)[0] ?? '', subTypesOf(b)[0] ?? '')
        : sort === 'city' ? blankLast(a.city ?? a.region ?? '', b.city ?? b.region ?? '')
          : sort === 'age' ? (ha.past_engagements === 0 ? 0 : 1) - (hb.past_engagements === 0 ? 0 : 1)
            : (hb.past_engagements - ha.past_engagements) || (hb.awarded - ha.awarded);
      return dir === 'asc' ? n : -n;
    });

  const counts = (data ?? []).reduce((a, c) => { a[rowState(c)]++; return a; }, { ok: 0, missing: 0, invalid: 0, duplicate: 0 } as Record<string, number>);
  // Filter-pill counts are over the UNFILTERED list, so you can see what a pill would bring back.
  const facets = useMemo(() => {
    const byType: Record<string, number> = {}; const byCity: Record<string, number> = {};
    let fresh = 0;
    for (const c of data ?? []) {
      const ts = subTypesOf(c);
      if (ts.length === 0) byType[NO_TYPE] = (byType[NO_TYPE] ?? 0) + 1;
      for (const t of ts) byType[t] = (byType[t] ?? 0) + 1;
      const k = c.city ?? NO_CITY; byCity[k] = (byCity[k] ?? 0) + 1;
      if (historyOf(c).past_engagements === 0) fresh++;
    }
    return { byType, byCity, fresh, old: (data ?? []).length - fresh };
  }, [data]);
  const filtersOn = typeSel.size > 0 || citySel.size > 0 || ageSel !== 'all';
  const toggle = (set: Set<string>, apply: (s: Set<string>) => void, v: string) => {
    const next = new Set(set);
    if (!next.delete(v)) next.add(v);
    apply(next);
  };

  if (isLoading) return <div className="card"><Skeleton lines={5} /></div>;

  return (
    <div>
      <h2 style={{ margin: '4px 0' }}>供应商名录 / Suppliers</h2>
      <p className="muted small" style={{ marginTop: 0 }}>
        劳务通过手机号登录门户。每家供应商可添加最多 5 名<strong>登录联系人</strong>（老板 + 经办人等），各用自己的手机号登录，
        操作会记录到人。下方标记出暂时无法登录的供应商。
        / Contractors sign in by phone — add up to 5 named contacts per supplier; actions are attributed to the person.
      </p>
      <div className="row-wrap" style={{ gap: 8, margin: '8px 0 12px', alignItems: 'center' }}>
        <span className="pill pill-good">可登录 / ready {counts.ok}</span>
        <span className={`pill ${counts.missing ? 'pill-warn' : 'pill-muted'}`}>缺号 / missing {counts.missing}</span>
        <span className={`pill ${counts.invalid ? 'pill-warn' : 'pill-muted'}`}>无效 / invalid {counts.invalid}</span>
        <span className={`pill ${counts.duplicate ? 'pill-bad' : 'pill-muted'}`}>重复 / duplicate {counts.duplicate}</span>
        <span style={{ flex: 1 }} />
        <input placeholder="搜索 / search" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 180 }} />
        <label className="small row" style={{ gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />只看待修正 / problems only
        </label>
      </div>

      <div className="row-wrap small" style={{ gap: 6, alignItems: 'center', margin: '0 0 12px' }}>
        {/* A pill nobody can use is just noise (Eric fb_edd6df83) — an EMPTY facet is hidden until
            something is tagged with it, but a pill you have already clicked never disappears. */}
        <span className="muted">类型 / type:</span>
        {[...subTypeOptions, NO_TYPE].filter((t) => (facets.byType[t] ?? 0) > 0 || typeSel.has(t)).map((t) => (
          <button key={t} style={PILL_BTN} className={`pill small ${typeSel.has(t) ? 'pill-stage' : 'pill-muted'}`}
            onClick={() => toggle(typeSel, setTypeSel, t)}>{t === NO_TYPE ? '未填 / unset' : t} {facets.byType[t] ?? 0}</button>
        ))}
        <span className="muted" style={{ marginLeft: 6 }}>城市 / city:</span>
        {[...cityOptions, NO_CITY].filter((city) => (facets.byCity[city] ?? 0) > 0 || citySel.has(city)).map((city) => (
          <button key={city} style={PILL_BTN} className={`pill small ${citySel.has(city) ? 'pill-stage' : 'pill-muted'}`}
            onClick={() => toggle(citySel, setCitySel, city)}>{city === NO_CITY ? '未填 / unset' : city} {facets.byCity[city] ?? 0}</button>
        ))}
        <span className="muted" style={{ marginLeft: 6 }}>新老 / new·old:</span>
        {([['all', `全部 / all ${(data ?? []).length}`], ['new', `新供应商 / new ${facets.fresh}`], ['old', `老供应商 / returning ${facets.old}`]] as const).map(([k, label]) => (
          <button key={k} style={PILL_BTN} className={`pill small ${ageSel === k ? 'pill-stage' : 'pill-muted'}`}
            onClick={() => setAgeSel(k)}>{label}</button>
        ))}
        <span style={{ flex: 1 }} />
        {/* label + select + direction + clear wrap as ONE unit — split across lines they read as a
            stray dropdown under the filter pills. */}
        <span className="row" style={{ gap: 6 }}>
          <span className="muted">排序 / sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} style={{ width: 'auto' }}>
            <option value="code">编号 / code</option>
            <option value="type">类型 / type</option>
            <option value="city">城市 / city</option>
            <option value="age">新老 / new·old</option>
            <option value="engagements">往来次数 / engagements</option>
          </select>
          <button className="small" style={{ padding: '0 6px' }} title="升序 / 降序 · asc / desc"
            onClick={() => setDir(dir === 'asc' ? 'desc' : 'asc')}>{dir === 'asc' ? '▲' : '▼'}</button>
          {filtersOn && (
            <button className="small muted" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => { setTypeSel(new Set()); setCitySel(new Set()); setAgeSel('all'); }}>清除 / clear</button>
          )}
        </span>
      </div>

      <div className="cmp-wrap"><table className="cmp"><thead><tr>
        <th>供应商 / Supplier</th><th>编号</th><th style={{ minWidth: 150 }}>类型 / Type</th><th>城市 / City</th>
        <th style={{ minWidth: 330 }}>登录联系人 / Login contacts</th><th>状态 / Login</th>
      </tr></thead><tbody>
        {rows.map((c) => (
          <SupplierRow key={c.id} c={c} state={rowState(c)} subTypeOptions={subTypeOptions} cityOptions={cityOptions} isGm={isGm} onSaved={refresh} />
        ))}
        {rows.length === 0 && <tr><td colSpan={6} className="muted small" style={{ padding: 12 }}>无匹配 / none</td></tr>}
      </tbody></table></div>
    </div>
  );
}

const BADGE: Record<string, { label: string; cls: string }> = {
  ok: { label: '✓ 可登录 / ready', cls: 'pill-good' },
  missing: { label: '缺号 / missing', cls: 'pill-warn' },
  invalid: { label: '无效 / invalid', cls: 'pill-warn' },
  duplicate: { label: '重复 / duplicate', cls: 'pill-bad' },
};

function errText(e: unknown, fallback: string): string {
  return (e as ApiError)?.body?.error ?? fallback;
}

function SupplierRow({ c, state, subTypeOptions, cityOptions, isGm, onSaved }: {
  c: RegistryLaborCo; state: 'ok' | 'missing' | 'invalid' | 'duplicate';
  subTypeOptions: string[]; cityOptions: string[]; isGm: boolean; onSaved: () => void;
}) {
  const contacts = c.contacts ?? [];
  const b = BADGE[state];
  const h = historyOf(c);
  return (
    <tr style={state !== 'ok' ? { background: 'var(--warn-soft)' } : undefined}>
      {/* 往来历史 is identity, not a 类型 — it lives under the name so the 类型 cell holds types only */}
      <td>
        <strong>{c.name}</strong>
        <div className="muted small">
          {h.past_engagements === 0 ? '新供应商 / new' : `往来 ${h.past_engagements} · 中标 ${h.awarded}`}
        </div>
      </td>
      <td className="muted small">{c.internal_code ?? '—'}</td>
      <td><TypeCell c={c} options={subTypeOptions} onSaved={onSaved} /></td>
      <td><CityCell c={c} options={cityOptions} onSaved={onSaved} /></td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {contacts.map((ct) => <ContactLine key={ct.id} ct={ct} onSaved={onSaved} />)}
          {contacts.length === 0 && <LegacyLine c={c} onSaved={onSaved} />}
          {contacts.length > 0 && contacts.length < 5 && <AddContactLine lcId={c.id} onSaved={onSaved} />}
        </div>
      </td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <span className={`pill small ${b.cls}`}>{b.label}</span>
          {isGm && <ReissuePasscode c={c} />}
        </div>
      </td>
    </tr>
  );
}

/**
 * gm 「🔑 重发口令」 — Eric 9/18 (reissue passcode): the passcode fallback for a supplier whose phone-SSO
 * isn't usable (or who just lost the code). The new code lives in THIS component's state and nowhere else:
 * no query cache, no localStorage, no re-fetch — a refresh loses it for good, which is the point.
 */
function ReissuePasscode({ c }: { c: RegistryLaborCo }) {
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ login_id: string; passcode: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function go() {
    if (!window.confirm(`为「${c.name}」生成新口令？旧口令立即失效。 / Issue a new passcode for ${c.name}? The old one stops working immediately.`)) return;
    setBusy(true);
    try {
      const r = await reissueLaborPasscode(c.id);
      setIssued({ login_id: r.login_id, passcode: r.passcode });
      setCopied(false);
    } catch (e) { toast(errText(e, '生成失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }

  async function copy() {
    if (!issued) return;
    // clipboard is unavailable over plain http and in some WeCom webviews — fall back SILENTLY to the
    // 口令 already on screen rather than nagging; the GM can still read it out.
    try {
      await navigator.clipboard?.writeText(`登录名 / login: ${issued.login_id}\n口令 / passcode: ${issued.passcode}`);
      setCopied(true);
    } catch { /* no clipboard — the code is visible above */ }
  }

  if (!issued) {
    return (
      <button className="small" style={{ padding: '0 6px' }} disabled={busy}
        title="生成新口令（旧口令立即失效）/ issue a new passcode — the old one stops working"
        onClick={go}>{busy ? '…' : '🔑 重发口令 / Reissue passcode'}</button>
    );
  }
  return (
    <div className="small" style={{
      display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start',
      border: '1px solid var(--rule)', borderRadius: 6, padding: '5px 7px', maxWidth: 260,
    }}>
      <span>登录名 / login: <code>{issued.login_id}</code></span>
      <span>口令 / passcode: <code>{issued.passcode}</code></span>
      <div className="row" style={{ gap: 4, alignItems: 'center' }}>
        <button className="small" style={{ padding: '0 6px' }} onClick={copy}>{copied ? '已复制 / copied' : '复制 / Copy'}</button>
        <button className="small muted" style={{ ...TEXT_BTN, padding: '0 4px' }} onClick={() => setIssued(null)}>收起 / hide</button>
      </div>
      <span className="muted" style={{ lineHeight: 1.35 }}>
        仅显示一次，请通过微信私发给劳务负责人；本页刷新后不再可见 / Shown once — send it privately to the supplier's contact; it is gone on refresh
      </span>
    </div>
  );
}

/** 二级类型 (多选). Click the cell to edit; an empty cell is a dashed ＋类型 chip and a filled one carries
 *  a ✎, so the column reads as editable at a glance (Eric fb_edd6df83). A type the GM has since removed
 *  from the list still shows so it can be unchecked rather than silently vanishing; 采购/估价/GM may also
 *  coin a brand-new type here, which the server appends to the shared list. */
function TypeCell({ c, options, onSaved }: { c: RegistryLaborCo; options: string[]; onSaved: () => void }) {
  const qc = useQueryClient();
  const subs = subTypesOf(c);
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState<string[]>(subs);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const boxes = [...options, ...subs.filter((t) => !options.includes(t))];
  const dirty = sel.length !== subs.length || sel.some((t) => !subs.includes(t));
  // A refetch can bring in someone else's save. Re-seed the local checkboxes from the server row so
  // the next 保存 doesn't silently write our stale list back over theirs.
  const subsKey = subs.join('|');
  useEffect(() => { setSel(subTypesOf(c)); }, [subsKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  async function save(next: string[]) {
    setBusy(true);
    try {
      await updateLaborCo(c.id, { sub_types: next });
      toast('已保存 / saved', { kind: 'success' });
      setSel(next); setAdding(false); setDraft(''); setEditing(false);
      qc.invalidateQueries({ queryKey: ['me'] });   // a newly coined type joins the shared list + filters
      onSaved();
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }

  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span className="muted small">选择类型（可多选）/ pick one or more</span>
        {boxes.map((t) => (
          <label key={t} className="small row" style={{ gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={sel.includes(t)}
              onChange={(e) => setSel(e.target.checked ? [...sel, t] : sel.filter((x) => x !== t))} />{t}
          </label>
        ))}
        {adding ? (
          // the new type is added AND ticked in one save — no second trip through the checkbox list
          <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="类型名" style={{ width: 88 }} />
            <button className="btn-primary small" disabled={busy || !draft.trim()}
              onClick={() => { const t = draft.trim(); save(sel.includes(t) ? sel : [...sel, t]); }}>{busy ? '…' : '保存'}</button>
            <button className="small" disabled={busy} onClick={() => { setAdding(false); setDraft(''); }}>取消</button>
          </div>
        ) : (
          <button className="small muted" style={{ ...TEXT_BTN, marginTop: 2 }} disabled={busy}
            onClick={() => setAdding(true)}>＋ 新类型 / new type…</button>
        )}
        {!adding && (
          <div className="row" style={{ gap: 4, marginTop: 2 }}>
            <button className="btn-primary small" disabled={busy || !dirty} onClick={() => save(sel)}>{busy ? '…' : '保存'}</button>
            <button className="small" disabled={busy} onClick={() => { setSel(subs); setEditing(false); }}>取消</button>
          </div>
        )}
      </div>
    );
  }
  return subs.length > 0 ? (
    <button className="row-wrap" title="点击编辑类型 / click to edit types" onClick={() => setEditing(true)}
      style={{ ...TEXT_BTN, gap: 4, alignItems: 'center' }}>
      {subs.map((t) => <span key={t} className="pill pill-stage small">{t}</span>)}
      <span className="muted small" title="编辑类型 / edit types">✎</span>
    </button>
  ) : (
    <button className="small muted" style={DASH_CHIP} title="点击编辑类型 / click to edit types"
      onClick={() => setEditing(true)}>＋ 类型 / set type</button>
  );
}

/** 城市 — click-to-edit (93 always-on dropdowns were the table's loudest noise, Eric fb_edd6df83): the
 *  cell is text until you click it, then it is the picker — pick from the shared list, or type a new one
 *  (采购/估价/GM may extend the list as they save; anyone else gets the server's 400 as a toast).
 *  `region` stays visible as the legacy 地区 hint. */
function CityCell({ c, options, onSaved }: { c: RegistryLaborCo; options: string[]; onSaved: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const choices = [...options, ...(c.city && !options.includes(c.city) ? [c.city] : [])];

  async function save(city: string | null) {
    setBusy(true);
    try {
      await updateLaborCo(c.id, { city });
      toast('已保存 / saved', { kind: 'success' });
      setAdding(false); setDraft(''); setOpen(false);
      qc.invalidateQueries({ queryKey: ['me'] });   // a newly added city joins the shared list
      onSaved();
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
      {adding ? (
        <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="城市名" style={{ width: 88 }} />
          <button className="btn-primary small" disabled={busy || !draft.trim()} onClick={() => save(draft.trim())}>{busy ? '…' : '保存'}</button>
          <button className="small" disabled={busy} onClick={() => { setAdding(false); setDraft(''); }}>取消</button>
        </div>
      ) : open ? (
        <select autoFocus value={c.city ?? ''} disabled={busy} style={{ width: 108 }}
          onBlur={() => setOpen(false)}
          onChange={(e) => { if (e.target.value === NEW_CITY) setAdding(true); else save(e.target.value || null); }}>
          <option value="">— 未填 —</option>
          {choices.map((city) => <option key={city} value={city}>{city}</option>)}
          <option value={NEW_CITY}>＋ 新城市 / new city…</option>
        </select>
      ) : c.city ? (
        <button style={TEXT_BTN} title="点击编辑城市 / click to edit city" onClick={() => setOpen(true)}>{c.city}</button>
      ) : (
        <button className="small muted" style={DASH_CHIP} title="点击编辑城市 / click to edit city"
          onClick={() => setOpen(true)}>＋ 城市 / set city</button>
      )}
      {!c.city && c.region && <span className="muted small">原地区：{c.region}</span>}
    </div>
  );
}

/** One editable contact: name · phone · role, 主 badge / 设为主, delete. */
function ContactLine({ ct, onSaved }: { ct: LaborCoContact; onSaved: () => void }) {
  const [name, setName] = useState(ct.name);
  const [phone, setPhone] = useState(ct.phone);
  const [role, setRole] = useState(ct.role_note ?? '');
  const [busy, setBusy] = useState(false);
  const dirty = name !== ct.name || phone !== ct.phone || role !== (ct.role_note ?? '');
  async function save() {
    setBusy(true);
    try {
      await updateLaborCoContact(ct.id, { name, phone, role_note: role || null });
      toast('已保存 / saved', { kind: 'success' }); onSaved();
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  async function makePrimary() {
    setBusy(true);
    try { await updateLaborCoContact(ct.id, { is_primary: true }); onSaved(); }
    catch (e) { toast(errText(e, '设置失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm(`删除联系人「${ct.name}」？其登录会话将立即失效。 / Delete this contact? Their login is revoked immediately.`)) return;
    setBusy(true);
    try { await deleteLaborCoContact(ct.id); toast('已删除 / deleted', { kind: 'success' }); onSaved(); }
    catch (e) { toast(errText(e, '删除失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {ct.is_primary
        ? <span className="pill small pill-good" title="主要联系人（名录显示此人）">主</span>
        : <button className="small" style={{ padding: '0 4px' }} disabled={busy} title="设为主要联系人" onClick={makePrimary}>设为主</button>}
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="姓名" style={{ width: 76 }} />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11 位手机号" style={{ width: 122 }} />
      <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="备注 (老板/对接…)" style={{ width: 104 }} />
      {dirty && <button className="btn-primary small" disabled={busy} onClick={save}>{busy ? '…' : '保存'}</button>}
      <button className="small" style={{ padding: '0 6px' }} disabled={busy} title="删除联系人" onClick={remove}>✕</button>
    </div>
  );
}

/** Inline add — ➕ expands to name/phone/role inputs; POST creates (server validates + dedups). */
function AddContactLine({ lcId, onSaved }: { lcId: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  if (!open) {
    return <button className="small muted" style={{ alignSelf: 'flex-start', padding: '0 6px' }} onClick={() => setOpen(true)}>＋ 添加联系人 / add contact</button>;
  }
  async function save() {
    setBusy(true);
    try {
      await addLaborCoContact(lcId, { name, phone, role_note: role || null });
      toast('已添加 / added', { kind: 'success' });
      setOpen(false); setName(''); setPhone(''); setRole('');
      onSaved();
    } catch (e) { toast(errText(e, '添加失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="姓名" style={{ width: 76 }} />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11 位手机号" style={{ width: 122 }} />
      <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="备注" style={{ width: 104 }} />
      <button className="btn-primary small" disabled={busy || !name.trim() || !phone.trim()} onClick={save}>{busy ? '…' : '保存'}</button>
      <button className="small" style={{ padding: '0 6px' }} disabled={busy} onClick={() => setOpen(false)}>取消</button>
    </div>
  );
}

/** A contact-less supplier: the original inline fix (name + phone) — saving enrolls the person as the
 *  primary login contact via the legacy PATCH (the server upserts the roster row). */
function LegacyLine({ c, onSaved }: { c: RegistryLaborCo; onSaved: () => void }) {
  const [phone, setPhone] = useState(c.phone ?? '');
  const [contact, setContact] = useState(c.primary_contact ?? '');
  const [busy, setBusy] = useState(false);
  const dirty = phone !== (c.phone ?? '') || contact !== (c.primary_contact ?? '');
  async function save() {
    setBusy(true);
    try {
      await updateLaborCo(c.id, { phone, primary_contact: contact });
      toast('已保存 / saved', { kind: 'success' }); onSaved();
    } catch (e) { toast(errText(e, '保存失败 / failed'), { kind: 'error' }); } finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="联系人" style={{ width: 76 }} />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="11 位手机号" style={{ width: 122, ...(phoneHealth(c.phone) !== 'ok' && !dirty ? { borderColor: 'var(--bad)' } : {}) }} />
      {dirty && <button className="btn-primary small" disabled={busy} onClick={save}>{busy ? '…' : '保存'}</button>}
    </div>
  );
}
